import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";
import { useAttorneyAutocompleteSearch, shouldRunAttorneySearch } from "../utils/attorneySearch";
import {
  buildBondAttorneySearchInputHandler,
  createBondAttorneySearchErrorHandler,
} from "../utils/bondAttorneySearchWiring";
import { isValidEmailAddress } from "../../../../globals/utils/emailUtils";
import {
  buildLawFirmFuzzyDialogResult,
  notifyExactLawFirmDuplicate,
  requestLawFirmFuzzyMatch,
} from "../utils/lawFirmFuzzyMatch";

export { parseFuzzyLawFirmMatch } from "../utils/lawFirmFuzzyMatch";

const EMPTY_ADDRESS = {
  address: "",
  zip: "",
  city: "",
  county: "",
  state: "",
  phone: "",
};

/**
 * Shared law-firm / attorney CRUD state and handlers (Phase 9.10B).
 * Surface-specific hooks compose on top via `surface` flag and callbacks.
 */
export function useAttorneyInformationCore({
  surface,
  cfg,
  data,
  updateData,
  onLawFirmCreated,
  onLawFirmSelected,
  onAddressCreated,
  onAttorneyCreated,
}) {
  const { role } = useSelector((state) => state.auth);
  const lawfirm = useSelector((state) => state.lawfirm);

  const useSearch = !!cfg.useDebouncedAttorneySearch;
  const searchLawFirmId =
    role === "law_firm_user" || role === "law_firm_admin"
      ? lawfirm.lawfirmId ?? 0
      : 0;

  const onSearchError = useCallback(createBondAttorneySearchErrorHandler(toast), []);

  const attorneySearch = useAttorneyAutocompleteSearch({
    apiClient,
    role,
    lawfirmId: searchLawFirmId,
    onSearchError,
  });

  const [attorneySearchInput, setAttorneySearchInput] = useState("");

  const handleAttorneySearchInputChange = useMemo(
    () =>
      useSearch
        ? buildBondAttorneySearchInputHandler(
            setAttorneySearchInput,
            attorneySearch.debouncedSearch
          )
        : undefined,
    [useSearch, attorneySearch.debouncedSearch]
  );

  const [lawFirms, setLawFirms] = useState([]);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [bulkAttorneys, setBulkAttorneys] = useState([]);
  const [loadingLawFirms, setLoadingLawFirms] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [bulkLoadingAttorneys, setBulkLoadingAttorneys] = useState(false);

  const attorneys = useSearch ? attorneySearch.attorneys : bulkAttorneys;
  const loadingAttorneys = useSearch ? attorneySearch.loadingAttorneys : bulkLoadingAttorneys;
  const setAttorneys = useSearch ? attorneySearch.clearAttorneys : setBulkAttorneys;

  const [openAttorneyDialog, setOpenAttorneyDialog] = useState(false);
  const [openLawFirmDialog, setOpenLawFirmDialog] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);

  const [newAttorney, setNewAttorney] = useState({ name: "", phone: "", email: "" });
  const [newLawFirm, setNewLawFirm] = useState({ name: "", phone: "" });
  const [newAddress, setNewAddress] = useState({ ...EMPTY_ADDRESS });

  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);

  const includeCustomerNumber = surface === "retrieval";
  const includeAttorneyDefault = surface === "creation" || surface === "historic";

  const fetchLawFirms = async (onLoaded) => {
    setLoadingLawFirms(true);
    try {
      const response = await apiClient("/list-lawfirms/", "GET");
      const formatted = response.map((firm) => ({
        label: firm.name,
        value: firm.lawfirm_id,
      }));
      setLawFirms(formatted);
      onLoaded?.(formatted);
    } catch (error) {
      console.error("Error fetching law firms:", error);
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const fetchLocationsByLawFirm = async (lawFirmId, options = {}) => {
    if (!lawFirmId) return;
    setLoadingLocations(true);
    try {
      const response = await apiClient(`/get-locations-by-lawfirm/${lawFirmId}`, "GET");
      const formatted = response.map((location) => ({
        label: location.name + " - " + location.address,
        value: location.law_firm_location_id,
        address: location.address,
        ...(includeCustomerNumber
          ? { customer_number: location.customer_number }
          : {}),
      }));
      setLawFirmLocations(formatted);
      options.onLoaded?.(formatted);
    } catch (error) {
      console.error("Error fetching law firm locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchAttorneysByLawFirm = async (lawFirmId, options = {}) => {
    if (useSearch) {
      attorneySearch.clearAttorneys();
      setAttorneySearchInput("");
      return;
    }
    if (!lawFirmId) return;
    setBulkLoadingAttorneys(true);
    try {
      const response = await apiClient(`/lawfirm-user-location/${lawFirmId}`, "GET");
      const formatted = response
        .filter((user) => user.Position === "Attorney")
        .map((attorney) => ({
          label: attorney.lawfirm_user_name,
          value: attorney.lawfirm_user_id,
          ...(includeAttorneyDefault ? { is_default: attorney.is_default } : {}),
        }));
      setBulkAttorneys(formatted);
      options.onLoaded?.(formatted);
    } catch (error) {
      console.error("Error fetching attorneys:", error);
    } finally {
      setBulkLoadingAttorneys(false);
    }
  };

  const clearAttorneySearch = useCallback(() => {
    if (useSearch) {
      attorneySearch.clearAttorneys();
      setAttorneySearchInput("");
    } else {
      setBulkAttorneys([]);
    }
  }, [useSearch, attorneySearch]);

  const handleOpenAttorneyDialog = () => setOpenAttorneyDialog(true);
  const handleCloseAttorneyDialog = () => setOpenAttorneyDialog(false);
  const handleOpenLawFirmDialog = () => setOpenLawFirmDialog(true);
  const handleCloseLawFirmDialog = () => setOpenLawFirmDialog(false);
  const handleOpenAddressDialog = () => setOpenAddressDialog(true);
  const handleCloseAddressDialog = () => setOpenAddressDialog(false);

  const applyLawFirmId = (lawFirmId) => {
    if (onLawFirmCreated) {
      onLawFirmCreated(lawFirmId);
      return;
    }
    if (cfg.clearRelatedOnLawFirmCreate) {
      updateData({
        ...data,
        lawFirm: lawFirmId,
        lawFirmAddress: null,
        attorneyOfRecord: null,
      });
      setLawFirmLocations([]);
      clearAttorneySearch();
    } else {
      updateData({ ...data, lawFirm: lawFirmId });
    }
  };

  const handleAddAttorney = async () => {
    const email = newAttorney.email?.trim() ?? "";
    if (!isValidEmailAddress(email)) {
      toast.error("Please enter a valid email address for the attorney.");
      return;
    }
    try {
      if (!useSearch) {
        setBulkLoadingAttorneys(true);
      }
      const response = await apiClient("/invite-lawfirm-user", "POST", {
        user: {
          email,
        },
        law_firm_location_id: data.lawFirmAddress,
        lawfirm_id: data.lawFirm,
        lawfirm_user_name: newAttorney.name,
        preferred_name: newAttorney.name,
        title: "Attorney",
        position: "Attorney",
        is_legal_service: false,
        is_admin: false,
        middle_name: newAttorney.name,
        phone: newAttorney.phone,
        is_active: true,
      });
      if (onAttorneyCreated) {
        onAttorneyCreated(response.lawfirm_user_id);
      } else {
        updateData({ ...data, attorneyOfRecord: response.lawfirm_user_id });
      }
      toast.success("Attorney added successfully!");
      handleCloseAttorneyDialog();
      if (useSearch && shouldRunAttorneySearch(attorneySearchInput)) {
        attorneySearch.debouncedSearch(attorneySearchInput);
      } else if (!useSearch) {
        void fetchAttorneysByLawFirm(data.lawFirm);
      }
    } catch (error) {
      console.error("Error adding attorney:", error);
      toast.error("Failed to add attorney!");
    } finally {
      if (!useSearch) {
        setBulkLoadingAttorneys(false);
      }
    }
  };

  const createLawFirm = async () => {
    const response = await apiClient("/create-a-lawfirm", "POST", {
      name: newLawFirm.name,
      website: "",
      logo: "",
      accounting_contact_full_name: "",
      accounting_contact_email: "",
      accounting_contact_phone: "",
      is_new: true,
      is_active: true,
      created_by_guest: true,
    });
    applyLawFirmId(response.lawfirm_id);
    toast.success("Law Firm added successfully!");
    handleCloseLawFirmDialog();
    void fetchLawFirms();
  };

  const handleAddLawFirm = async () => {
    try {
      setLoadingLawFirms(true);
      const parseResult = await requestLawFirmFuzzyMatch(apiClient, newLawFirm.name);
      const dialogResult = buildLawFirmFuzzyDialogResult(parseResult, newLawFirm.name);
      if (dialogResult) {
        setLoadingLawFirms(false);
        setFuzzyMatchResult(dialogResult);
        setFuzzyMatchDialogOpen(true);
        return;
      }
      await createLawFirm();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const handleUseExistingLawFirm = () => {
    if (!fuzzyMatchResult) return;
    if (onLawFirmSelected) {
      onLawFirmSelected(fuzzyMatchResult.id);
    } else {
      applyLawFirmId(fuzzyMatchResult.id);
    }
    void fetchLawFirms();
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    handleCloseLawFirmDialog();
    toast.success("Using existing law firm.");
  };

  const handleCreateNewLawFirmAnyway = async () => {
    if (notifyExactLawFirmDuplicate(fuzzyMatchResult?.exactMatch, toast)) {
      return;
    }
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoadingLawFirms(true);
      await createLawFirm();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const postNewLocation = async () => {
    const response = await apiClient("/lawfirm-location/", "POST", [
      {
        lawfirm_id: data.lawFirm,
        name: "Guest Location",
        address: newAddress.address,
        zip_code: newAddress.zip,
        city: newAddress.city,
        county: newAddress.county,
        state: newAddress.state,
        phone: newAddress.phone,
        is_active: true,
      },
    ]);
    const locationId = response[0].law_firm_location_id;
    if (onAddressCreated) {
      onAddressCreated(locationId);
    } else {
      updateData({ ...data, lawFirmAddress: locationId });
    }
    toast.success("Address added successfully!");
    handleCloseAddressDialog();
    void fetchLocationsByLawFirm(data.lawFirm);
  };

  const handleAddAddress = async () => {
    try {
      setLoadingLocations(true);
      await postNewLocation();
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("Failed to add address!");
    } finally {
      setLoadingLocations(false);
    }
  };

  const selectedLocation = lawFirmLocations.find(
    (loc) => loc.value === data.lawFirmAddress
  );

  return {
    lawFirms,
    setLawFirms,
    lawFirmLocations,
    setLawFirmLocations,
    attorneys,
    setAttorneys,
    loadingLawFirms,
    loadingLocations,
    setLoadingLocations,
    loadingAttorneys,
    openAttorneyDialog,
    openLawFirmDialog,
    openAddressDialog,
    newAttorney,
    setNewAttorney,
    newLawFirm,
    setNewLawFirm,
    newAddress,
    setNewAddress,
    fuzzyMatchDialogOpen,
    setFuzzyMatchDialogOpen,
    fuzzyMatchResult,
    setFuzzyMatchResult,
    fetchLawFirms,
    fetchLocationsByLawFirm,
    fetchAttorneysByLawFirm,
    handleOpenAttorneyDialog,
    handleCloseAttorneyDialog,
    handleOpenLawFirmDialog,
    handleCloseLawFirmDialog,
    handleOpenAddressDialog,
    handleCloseAddressDialog,
    handleAddAttorney,
    handleAddLawFirm,
    handleUseExistingLawFirm,
    handleCreateNewLawFirmAnyway,
    handleAddAddress,
    postNewLocation,
    selectedLocation,
    attorneySearchInput,
    setAttorneySearchInput,
    handleAttorneySearchInputChange,
    clearAttorneySearch,
  };
}
